import { useState, useEffect, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Pages rendered on either side of the one being read. */
const PAGE_WINDOW = 1;
/**
 * Below this length, every page renders and the window never engages. A
 * document this short costs little to hold whole, and skipping the machinery
 * keeps the common case — a two-page estimate — behaving exactly as before.
 */
const WINDOWING_THRESHOLD = 3;
/** US Letter portrait, used until a real page has been measured. */
const FALLBACK_PAGE_ASPECT = 11 / 8.5;

interface PdfViewerProps {
  /** A presigned S3 URL or any remote URL. The component fetches it as a blob to avoid CORS issues with pdfjs. */
  url: string;
  className?: string;
}

/**
 * Renders a PDF from a URL using react-pdf (pdfjs-dist).
 * Fetches the PDF as a blob first to avoid pdfjs CORS restrictions on presigned S3 URLs.
 * Pages scale to fit the container width exactly.
 *
 * Every page has a slot in the stack so the document scrolls the way a reader
 * expects. It used to mount only `currentPage`, which left a multi-page
 * estimate feeling stuck: the wheel did nothing because there was nothing
 * below the fold, and the only way through was noticing two small arrows.
 * The counter now follows the scroll position and the arrows jump between
 * pages, so both ways of moving through the document agree.
 *
 * Every page mounts, but only those near the one being read carry a canvas —
 * the part that runs to megabytes. The rest keep their text layer and reserve
 * their height, so a long document costs a fixed handful of canvases while
 * browser find, select-all and copy still reach all of it.
 */
export default function PdfViewer({ url, className }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState(false);
  // Height/width for EVERY page, read from the document itself. Indexed by page
  // number less one; see `heightForPage` for why this is per page.
  const [pageAspects, setPageAspects] = useState<number[]>([]);
  // Guards the async read below against a document that has been replaced.
  const aspectToken = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    let objectUrl: string | null = null;
    // A slow request must not win after the document has moved on. Cleanup only
    // revoked a blob that already existed, so an in-flight fetch for the
    // previous url could resolve last, call setBlobUrl, and put the wrong
    // signed document back on screen — and leak its blob, since its own
    // cleanup had already run. Abort the request and ignore anything that
    // still arrives.
    const controller = new AbortController();
    let cancelled = false;
    setError(false);
    setBlobUrl(null);
    fetch(url, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch PDF');
        return r.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        // An abort is this component moving on, not a failed download.
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [url]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Refs are keyed by page index, so a new document has to clear them: loading
  // a shorter PDF would otherwise leave stale nodes past its last page, and the
  // observer below would resolve one of them to an out-of-range page number.
  // Keyed on `url` rather than reset in `onLoadSuccess`, which re-fires after
  // the ref callbacks have already run and would wipe live refs.
  useEffect(() => {
    pageRefs.current = [];
    // Pagination belongs to the document that is on screen, so it has to go
    // with it. Page COUNT especially: rendering is `Array.from({ length:
    // numPages })`, so carrying a 5-page count into a 2-page document mounts
    // pages 3-5 against it for the frame between the new blob committing and
    // onLoadSuccess correcting the count — out-of-range pages react-pdf has no
    // way to render. Zero means nothing renders until the new document reports
    // its own length.
    setNumPages(0);
    setCurrentPage(1);
    // Paper sizes belong to the document too, and any read still in flight for
    // the previous one has to lose — the same class of staleness as the page
    // count above.
    aspectToken.current += 1;
    setPageAspects([]);
  }, [url]);

  // Only pages near the one being read carry a canvas; the rest hold their
  // place as blank sheets. Mounting all of them is what made the document
  // scrollable, but the count is unbounded — estimates paginate with line
  // items and with embedded attachment photos, which have no cap — and three
  // of the four surfaces using this viewer are dialogs that mount and unmount
  // repeatedly through a session.
  //
  // The wrapper divs stay mounted either way. Only their contents swap, so the
  // refs, the IntersectionObserver registrations and the arrow targets below
  // all keep pointing at the same nodes as the window slides.
  const isPageRendered = (page: number) =>
    numPages <= WINDOWING_THRESHOLD || Math.abs(page - currentPage) <= PAGE_WINDOW;

  // Paper size comes from the document itself, not from assuming US Letter and
  // not from measuring the screen. pdfjs knows every page's geometry as soon as
  // the file loads — `getViewport` has the page's own `/Rotate` already applied
  // — so a placeholder is exact from the first frame, including for a page that
  // never paints because the reader scrolled past it.
  //
  // Measuring a painted canvas, which this used to do, could only ever learn
  // about pages that had already rendered, and only one page at a time.
  const readPageSizes = async (pdf: {
    numPages: number;
    getPage?: (
      page: number,
    ) => Promise<{ getViewport: (o: { scale: number }) => { width: number; height: number } }>;
  }) => {
    if (typeof pdf.getPage !== 'function') return;
    // BOUND. `getPage` is a method on the pdfjs document proxy and uses `this`,
    // so calling it detached rejects for every page — and because each page
    // falls back on its own, that failure is silent: every placeholder quietly
    // takes US Letter and the document looks like one whose geometry simply
    // could not be read. Cost a browser check to find, since a mock supplying
    // `getPage` as an arrow function needs no `this` and cannot reproduce it.
    const getPage = pdf.getPage.bind(pdf);
    // Bumped here as well as on `url`, so a second read for the same document —
    // react-pdf re-reporting a load — cannot land after this one.
    aspectToken.current += 1;
    const token = aspectToken.current;
    const aspects = await Promise.all(
      Array.from({ length: pdf.numPages }, async (_, i) => {
        try {
          const { width, height } = (await getPage(i + 1)).getViewport({ scale: 1 });
          return width > 0 && height > 0 ? height / width : FALLBACK_PAGE_ASPECT;
        } catch {
          // One unreadable page must not cost the rest of the document its
          // geometry, so it takes the fallback on its own.
          return FALLBACK_PAGE_ASPECT;
        }
      }),
    );
    // A different document may have loaded while this was in flight.
    if (aspectToken.current === token) setPageAspects(aspects);
  };

  // Keep the counter honest while the reader scrolls. Watching intersection
  // against the viewport (rather than a scroll handler on a container) is what
  // lets the page itself be the scroller, which is how this viewer is embedded
  // everywhere it is used.
  //
  // Keyed on `blobUrl` as well as `numPages`: swapping to a different document
  // of the SAME length leaves numPages untouched, so keying on it alone left
  // the observer watching the previous document's detached nodes and never
  // watching the new ones — the counter silently stopped following the scroll.
  // blobUrl changes for every document, and it changes after the new pages
  // have mounted and re-populated the refs this effect observes.
  useEffect(() => {
    if (numPages < 2) return;
    // Every page's most recent share of the viewport. A callback batch carries
    // ONLY the pages whose visibility changed, so choosing the best within the
    // batch reports whichever page happened to cross a threshold rather than
    // the one being read: nudge a full-screen page 1 until page 2's edge
    // appears and the batch contains page 2 alone, at ratio 0.1, which then
    // wins by default. Holding every ratio lets the winner be chosen across
    // the whole document.
    const ratios = new Map<Element, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          ratios.set(entry.target, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        let bestEl: Element | null = null;
        let bestRatio = 0;
        for (const [el, ratio] of ratios) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestEl = el;
          }
        }
        if (!bestEl) return;
        const index = pageRefs.current.indexOf(bestEl as HTMLDivElement);
        if (index >= 0) setCurrentPage(index + 1);
      },
      // A page taller than the viewport never reaches a high ratio, so the
      // steps stay fine-grained enough to separate two partly-visible pages.
      { threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] },
    );
    pageRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [numPages, blobUrl]);

  const goToPage = (page: number) => {
    if (!pageRefs.current[page - 1]) return;
    setCurrentPage(page);
    // Scroll after the state commit, re-reading the ref rather than closing
    // over it. Calling scrollIntoView inline alongside setCurrentPage looked
    // right and passed a scripted click, but on a real one the re-render lands
    // between the two and the scroll never happens: the counter advanced to
    // the next page while the document sat still.
    requestAnimationFrame(() => {
      pageRefs.current[page - 1]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  if (error) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        Failed to load PDF
      </div>
    );
  }

  const pagePadding = 24;
  // Clamped: a container narrower than its own padding would otherwise give
  // every page a negative width, and now a negative reserved height with it.
  const pageWidth = Math.max(0, (containerWidth ?? 0) - pagePadding * 2);
  // Height reserved for one page — used both for the blank sheet a withheld
  // page shows and for the box a rendering page shows while its canvas paints,
  // so nothing shifts under the reader as the window slides.
  //
  // Per page rather than one number for the document. A single aspect meant
  // learning any page's size re-sized every placeholder at once, so on a PDF
  // that mixes portrait and landscape, scrolling from a portrait page onto a
  // landscape one re-sized the whole document under the reader: a 12-page
  // alternating file lost 3,042px in a single frame, taking every scroll
  // position below the window and the counter with it.
  const heightForPage = (page: number) =>
    pageWidth * (pageAspects[page - 1] ?? FALLBACK_PAGE_ASPECT);

  return (
    // `contain: inline-size` breaks a measurement loop. The pages are sized from
    // this box's width, and without containment the box's width was sized from
    // the pages: in a grid or flex parent (every dialog) its automatic minimum
    // is its content's width, so it could never shrink below the last page
    // drawn. The document overflowed its dialog sideways, and every scrollbar
    // that came or went re-measured, redrew the canvas blank and measured again.
    // Containment makes the width come from the parent alone. It is not
    // `overflow-hidden`, so the sticky toolbar below keeps the viewport.
    <div ref={containerRef} className={className} style={{ contain: 'inline-size' }}>
      {!blobUrl || containerWidth === null ? (
        <div className="flex h-48 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        // No `overflow-hidden` on this container on purpose: it would make the
        // box a scroll container, which captures the sticky toolbar below and
        // pins it to this box instead of the viewport — the controls then slide
        // off screen with the document, which is what made them unreachable.
        // The toolbar rounds its own top corners since nothing clips them now.
        <div className="bg-[#525659] rounded-lg flex flex-col">
          {/* Toolbar. Always present, including on a one-page document: every
              estimate has to render the same shell, and hiding it there made
              two quotes for the same tenant look like two different products.
              Sticky so the controls stay reachable while the reader scrolls. */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg px-4 py-2 bg-[#3c3f41] shrink-0">
            <button
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              aria-label="Previous page"
              className="flex items-center justify-center w-7 h-7 rounded text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>

            <span className="text-xs text-gray-300">
              {numPages > 0 ? `${currentPage} / ${numPages}` : '—'}
            </span>

            <button
              onClick={() => goToPage(Math.min(numPages, currentPage + 1))}
              disabled={currentPage >= numPages}
              aria-label="Next page"
              className="flex items-center justify-center w-7 h-7 rounded text-gray-300 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Every page, stacked — the document scrolls instead of paging. */}
          <div style={{ padding: pagePadding }}>
            <Document
              file={blobUrl}
              onLoadSuccess={(pdf) => {
                setNumPages(pdf.numPages);
                setCurrentPage(1);
                void readPageSizes(pdf);
              }}
              loading={
                <div className="flex h-48 items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                </div>
              }
              className="flex flex-col items-center gap-6"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    pageRefs.current[i] = el;
                  }}
                  // The slot, not the page. This node is what the refs, the
                  // observer and the arrows all address, and it stays put
                  // whether or not a page is rendered into it.
                  data-testid={`pdf-slot-${i + 1}`}
                  // Clears the sticky toolbar when an arrow jumps to this page.
                  style={{
                    // The slot carries the paper's width, not its contents.
                    // `Document` lays these out with `align-items: center`, so
                    // a flex item is sized to fit-content: a placeholder — a
                    // div with a height and nothing in it — measured ZERO wide,
                    // leaving the background and shadow below nothing to paint.
                    // A withheld page showed as a bare sliver against the
                    // backdrop instead of a blank sheet, and a zero-AREA box
                    // also reports intersectionRatio 1 to the observer above,
                    // so it could outrank the page actually being read.
                    width: pageWidth,
                    // A withheld page carries a text layer but no canvas, and
                    // a text layer is absolutely positioned — it contributes
                    // no height at all. Without this the stack collapses to
                    // the two rendered pages, the scrollbar claims the
                    // document is nearly over, and every scroll position below
                    // the window is meaningless. Rendered pages size
                    // themselves, so this only floors the withheld ones.
                    minHeight: isPageRendered(i + 1) ? undefined : heightForPage(i + 1),
                    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                    background: '#fff',
                    lineHeight: 0,
                    scrollMarginTop: 56,
                  }}
                >
                  {isPageRendered(i + 1) ? (
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderTextLayer
                      renderAnnotationLayer
                      loading={<div style={{ height: heightForPage(i + 1) }} />}
                    />
                  ) : (
                    // Withheld: no canvas, but the text layer still mounts.
                    // The canvas is what costs megabytes; the text layer is
                    // DOM. Dropping both meant browser find, select-all and
                    // copy reached only the two or three pages on screen —
                    // a customer searching a signed estimate for a term in
                    // the terms block got no matches at all.
                    <Page
                      pageNumber={i + 1}
                      width={pageWidth}
                      renderMode="none"
                      renderTextLayer
                      renderAnnotationLayer={false}
                      loading={<div style={{ height: heightForPage(i + 1) }} />}
                    />
                  )}
                </div>
              ))}
            </Document>
          </div>
        </div>
      )}
    </div>
  );
}
